import { useCallback, useEffect, useRef, useState } from 'react';
import { EidEasySignStatus } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
import {
	DrawnSignatureSource,
	EidEasySigningMethod,
} from '@dealstack/shared/lib/common/eideasy/signingMethods';
import { SignerContactType } from '@dealstack/shared/lib/generated';
import {
	ensureError,
	FTN_CALLBACK_PATH,
	makeEidEasyClient,
	openSdkRedirectTab,
	REDIRECT_METHODS,
	runFrejaSdkSignature,
	toErrorKey,
	type EidEasySdkClient,
} from './useEidEasySign.utils';
import { useEidEasyOtpFlow } from './useEidEasyOtpFlow';
import type {
	EidEasySignCoreConfig,
	PendingMethodContext,
	PrepareExtras,
} from './useEidEasySignCore.types';

// Freja eID is a Swedish device-based method; the SDK echoes this as `country`.
const FREJA_COUNTRY_CODE = 'SE';

/**
 * Shared core of the eID Easy signing state machine. Owns the status/error/
 * challenge state and the flows whose mechanism is identical across both signing
 * surfaces — OAuth2 redirect (Swedish/Norwegian BankID, Danish MitID), Freja eID,
 * OTP and the handwritten Draw flow. The OTP sub-flow lives in `useEidEasyOtpFlow`
 * (composed below); the redirect/Freja/Draw flows share the `runSignFlow`
 * pipeline (prepare → SDK step → complete → done).
 *
 * All non-DRAW methods gate through a SETUP_SIGNATURE step IMMEDIATELY on method
 * selection, before any method-specific input. The captured drawn signature is
 * then included in the `prepare` call as `drawnSignatureBase64`.
 *
 * Flow for methods with an input step (Smart-ID, OTP, Freja):
 *   method-select → SETUP_SIGNATURE → method-specific input → prepare → done
 *
 * Flow for methods without an input step (FTN, BankID, MitID):
 *   method-select → SETUP_SIGNATURE → prepare → done
 *
 * Everything that genuinely differs between the in-app and public flows is
 * injected via `config.transport` (where the prepare/complete requests go) and
 * `config.captureError` (the Sentry tagging + user-cancellation policy). The two
 * methods whose *mechanism* differs — Smart-ID and FTN — live in the wrapper
 * hooks and drive this core's state via the exposed setters and `runFlow`.
 */
export const useEidEasySignCore = ({
	transport,
	captureError,
	onComplete,
	initialStatus = EidEasySignStatus.IDLE,
}: EidEasySignCoreConfig) => {
	const [status, setStatus] = useState<EidEasySignStatus>(initialStatus);
	const [challengeCode, setChallengeCode] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [pendingContext, setPendingContext] = useState<PendingMethodContext | null>(null);
	/** Set by `onSignatureConfirmed` when the user has drawn their signature during SETUP_SIGNATURE.
	 * Read by the method-specific confirm functions (confirmFreja, confirmOtp, etc.) which are
	 * called from the input step that follows SETUP_SIGNATURE. */
	const [capturedSignature, setCapturedSignature] = useState<string | null>(null);
	/** Set by `selectMethod`-equivalents to indicate which method the user picked.
	 * Consumed by `onSignatureConfirmed` to know where to route after SETUP_SIGNATURE. */
	const [pendingMethodSelection, setPendingMethodSelection] =
		useState<EidEasySigningMethod | null>(null);

	// Each signing flow is a long-lived async chain. If the consuming component
	// unmounts mid-flow, skip further state mutations to avoid React warnings.
	const unmountedRef = useRef(false);
	useEffect(() => {
		unmountedRef.current = false;
		return () => {
			unmountedRef.current = true;
		};
	}, []);

	/** Runs a flow body, routing any throw to the surface's error policy + ERROR status. */
	const runFlow = useCallback(
		async (phase: string, fn: () => Promise<void>) => {
			try {
				await fn();
			} catch (err: unknown) {
				captureError(err, phase);
				if (unmountedRef.current) {
					return;
				}
				setError(toErrorKey(err));
				setStatus(EidEasySignStatus.ERROR);
			}
		},
		[captureError]
	);

	// ─── OTP sub-flow (prepare/dispatch + inline-retry submit + resend) ────────
	const {
		runOtpFlow,
		submitOtpCode,
		resendOtpCode,
		resetOtpContext,
		otpVerifying,
		otpCodeError,
		otpChannel,
		otpMaskedContact,
	} = useEidEasyOtpFlow({
		transport,
		captureError,
		onComplete,
		unmountedRef,
		setStatus,
		setError,
		runFlow,
	});

	/**
	 * Shared pipeline for the methods whose mechanism is "prepare → (optional SDK
	 * step) → complete → done": redirect (BankID/MitID), Freja, and Draw. Each
	 * supplies only its `sdkStep`; mockMode skips the SDK entirely.
	 */
	const runSignFlow = useCallback(
		async (
			method: EidEasySigningMethod,
			phase: string,
			extras: PrepareExtras,
			sdkStep?: (client: EidEasySdkClient) => Promise<void>,
			clientOpts?: { redirectUri?: string }
		) => {
			setStatus(EidEasySignStatus.PREPARING);
			setError(null);

			await runFlow(phase, async () => {
				const prep = await transport.prepare(method, extras);

				if (!prep.mockMode && sdkStep) {
					setStatus(EidEasySignStatus.AWAITING_SIGN);
					await sdkStep(makeEidEasyClient(prep, clientOpts));
				}

				if (unmountedRef.current) {
					return;
				}
				setStatus(EidEasySignStatus.COMPLETING);
				const result = await transport.complete(prep.docId);

				if (unmountedRef.current) {
					return;
				}
				setStatus(EidEasySignStatus.DONE);
				onComplete?.(result);
			});
		},
		[transport, runFlow, onComplete]
	);

	// ─── Redirect-based methods (SE BankID, NO BankID, MitID) ───────────────

	/** Called by `onSignatureConfirmed` for redirect-method contexts. */
	const runRedirectFlow = useCallback(
		async (method: EidEasySigningMethod, signatureBase64: string) => {
			const config = REDIRECT_METHODS[method];
			if (!config) {
				captureError(
					new Error(`No redirect signing config for method "${method}"`),
					method
				);
				return;
			}

			const callbackUrl = `${window.location.origin}${FTN_CALLBACK_PATH}`;
			await runSignFlow(
				method,
				method,
				{ signatureRedirect: callbackUrl, drawnSignatureBase64: signatureBase64 },
				async (client) => {
					const { redirect, completed, fail } = openSdkRedirectTab();
					client.signature[config.launcher].start({
						countryCode: config.countryCode,
						redirect,
						fail,
					});
					await completed;
				},
				{ redirectUri: callbackUrl }
			);
		},
		[runSignFlow, captureError]
	);

	/**
	 * Called from the redirect-method input (if any) after the user's signature has
	 * already been captured. Reads `capturedSignature` from state and calls
	 * `runRedirectFlow` directly — no second SETUP_SIGNATURE pass.
	 *
	 * In the current flow, redirect methods have no input step, so this is invoked
	 * directly from `onSignatureConfirmed` in the wrapper. It is exposed so that
	 * the wrapper can call it after SETUP_SIGNATURE completes.
	 */
	const confirmRedirectMethod = useCallback(
		(method: EidEasySigningMethod) => {
			if (!transport.canSign()) {
				return;
			}
			const isRedirect =
				method === EidEasySigningMethod.SE_BANKID ||
				method === EidEasySigningMethod.NORWEGIAN_BANKID ||
				method === EidEasySigningMethod.MITID;
			if (!isRedirect) {
				captureError(
					new Error(`No redirect signing config for method "${method}"`),
					method
				);
				return;
			}
			setPendingContext({ method } as PendingMethodContext);
			setStatus(EidEasySignStatus.SETUP_SIGNATURE);
		},
		[transport, captureError]
	);

	// ─── Freja ───────────────────────────────────────────────────────────────

	/** Called by `onSignatureConfirmed` for Freja context. */
	const runFrejaFlow = useCallback(
		async (email: string, signatureBase64: string) => {
			setChallengeCode(null);
			await runSignFlow(
				EidEasySigningMethod.FREJA,
				'freja',
				{ drawnSignatureBase64: signatureBase64 },
				async (client) => {
					await runFrejaSdkSignature(client, {
						email,
						// countryCode is required by the SDK. Hardcoded SE: Freja eID is
						// a Swedish service. The signer is identified by email.
						countryCode: FREJA_COUNTRY_CODE,
						onChallenge: setChallengeCode,
					});
					setChallengeCode(null);
				}
			);
		},
		[runSignFlow]
	);

	/**
	 * Called from the Freja input step after the user's signature has already been
	 * captured in `capturedSignature`. Calls `runFrejaFlow` directly with the
	 * cached signature — no second SETUP_SIGNATURE pass.
	 */
	const confirmFreja = useCallback(
		(email: string) => {
			if (!transport.canSign()) {
				return;
			}
			if (!capturedSignature) {
				// No cached signature — fall back to the old path: set pendingContext and
				// advance to SETUP_SIGNATURE so the user draws their signature.
				setPendingContext({ method: EidEasySigningMethod.FREJA, email });
				setStatus(EidEasySignStatus.SETUP_SIGNATURE);
				return;
			}
			const sig = capturedSignature;
			setCapturedSignature(null);
			void runFrejaFlow(email, sig);
		},
		[transport, capturedSignature, runFrejaFlow]
	);

	// ─── OTP (signature-capture bridge to the OTP sub-flow) ────────────────────

	/**
	 * Called from the OTP input step after the user's signature has already been
	 * captured in `capturedSignature`. Hands the cached signature to the OTP
	 * sub-flow's `runOtpFlow` — no second SETUP_SIGNATURE pass.
	 */
	const confirmOtp = useCallback(
		(channel: SignerContactType, value: string) => {
			if (!transport.canSign()) {
				return;
			}
			if (!capturedSignature) {
				// No cached signature — fall back to the old path.
				setPendingContext({ method: EidEasySigningMethod.OTP, channel, value });
				setStatus(EidEasySignStatus.SETUP_SIGNATURE);
				return;
			}
			const sig = capturedSignature;
			setCapturedSignature(null);
			void runOtpFlow(channel, value, sig);
		},
		[transport, capturedSignature, runOtpFlow]
	);

	// ─── Draw / handwritten-simple-signature ─────────────────────────────────

	const confirmSignature = useCallback(
		async (signatureDataUrl: string, source: DrawnSignatureSource) => {
			if (!transport.canSign()) {
				return;
			}
			await runSignFlow(
				EidEasySigningMethod.DRAW,
				'draw',
				{ drawnSignatureBase64: signatureDataUrl, drawnSignatureSource: source },
				async (client) => {
					await new Promise<void>((resolve, reject) => {
						client.signature.handwrittenSignature.start({
							handwrittenSignatureDataUrl: signatureDataUrl,
							success: () => resolve(),
							fail: (failError: unknown) => reject(ensureError(failError)),
						});
					});
				}
			);
		},
		[transport, runSignFlow]
	);

	// ─── Shared signature-confirmed handler ──────────────────────────────────

	/**
	 * Called by `SetupSignatureStep` once the user has captured their signature.
	 *
	 * Two paths:
	 *
	 * 1. **Method-selection path** (`pendingMethodSelection` is set): the user just
	 *    selected a method and was sent straight to SETUP_SIGNATURE. Cache the
	 *    drawn signature, then:
	 *    - Methods WITH an input step (Smart-ID, OTP, Freja): advance to the
	 *      method's input step so the user can enter their idcode / contact.
	 *    - Methods WITHOUT an input step (FTN, BankID, MitID): kick off the
	 *      prepare flow immediately with the captured signature.
	 *
	 * 2. **Legacy / pendingContext path** (`pendingContext` is set): retained for
	 *    backward-compatibility in cases where a wrapper has explicitly staged a
	 *    `pendingContext`. Currently only used by `confirmRedirectMethod` until
	 *    wrappers are fully migrated.
	 */
	const onSignatureConfirmed = useCallback(
		async (signatureBase64: string) => {
			// Path 1: came directly from method-select via pendingMethodSelection.
			if (pendingMethodSelection !== null) {
				const method = pendingMethodSelection;
				setPendingMethodSelection(null);

				switch (method) {
					case EidEasySigningMethod.SMART_ID:
						// Smart-ID needs idcode + country — store signature and advance to input step.
						setCapturedSignature(signatureBase64);
						setStatus(EidEasySignStatus.SMART_ID_INPUT);
						return;
					case EidEasySigningMethod.OTP:
						// OTP needs contact details — store signature and advance to input step.
						setCapturedSignature(signatureBase64);
						setStatus(EidEasySignStatus.OTP_INPUT);
						return;
					case EidEasySigningMethod.FREJA:
						// Freja needs the signer's email + phone — store signature and advance to input step.
						setCapturedSignature(signatureBase64);
						setStatus(EidEasySignStatus.FREJA_INPUT);
						return;
					case EidEasySigningMethod.FTN:
						// FTN has no input step — wrapper's runFtnFlow is called from here.
						// The wrapper overrides onSignatureConfirmed and handles this branch.
						// If we reach this default case without a wrapper override, error.
						captureError(
							new Error(`onSignatureConfirmed reached core for FTN method`),
							'setup-signature'
						);
						setStatus(EidEasySignStatus.ERROR);
						return;
					case EidEasySigningMethod.SE_BANKID:
					case EidEasySigningMethod.NORWEGIAN_BANKID:
					case EidEasySigningMethod.MITID:
						// Redirect methods have no input step — run immediately.
						await runRedirectFlow(method, signatureBase64);
						return;
					default:
						captureError(
							new Error(
								`onSignatureConfirmed: unhandled pendingMethodSelection "${method}"`
							),
							'setup-signature'
						);
						setStatus(EidEasySignStatus.ERROR);
						return;
				}
			}

			// Path 2: pendingContext was set explicitly (Freja/OTP/redirect via old path,
			// or redirect methods via confirmRedirectMethod).
			if (!pendingContext) {
				setError('eid_easy_signing.error_generic');
				setStatus(EidEasySignStatus.ERROR);
				return;
			}

			const ctx = pendingContext;
			setPendingContext(null);

			switch (ctx.method) {
				case EidEasySigningMethod.FREJA:
					await runFrejaFlow(ctx.email, signatureBase64);
					break;
				case EidEasySigningMethod.OTP:
					await runOtpFlow(ctx.channel, ctx.value, signatureBase64);
					break;
				case EidEasySigningMethod.SE_BANKID:
				case EidEasySigningMethod.NORWEGIAN_BANKID:
				case EidEasySigningMethod.MITID:
					await runRedirectFlow(ctx.method, signatureBase64);
					break;
				default:
					// FTN and Smart-ID have wrapper-level flows; they set their own
					// pending context and call their own run* functions after confirming
					// the signature. This branch is reached when the wrapper has NOT
					// overridden onSignatureConfirmed — it should not happen in practice.
					captureError(
						new Error(`onSignatureConfirmed reached core for method "${ctx.method}"`),
						'setup-signature'
					);
					setStatus(EidEasySignStatus.ERROR);
			}
		},
		[
			pendingMethodSelection,
			pendingContext,
			runFrejaFlow,
			runOtpFlow,
			runRedirectFlow,
			captureError,
		]
	);

	return {
		// state
		status,
		challengeCode,
		error,
		// state setters (consumed by the wrappers' bespoke Smart-ID / FTN flows)
		setStatus,
		setError,
		setChallengeCode,
		unmountedRef,
		runFlow,
		// pending context (consumed by wrappers for Smart-ID / FTN)
		pendingContext,
		setPendingContext,
		// method-selection state (new flow: method-select → SETUP_SIGNATURE first)
		pendingMethodSelection,
		setPendingMethodSelection,
		capturedSignature,
		setCapturedSignature,
		// shared flows
		confirmRedirectMethod,
		confirmFreja,
		confirmOtp,
		confirmSignature,
		onSignatureConfirmed,
		// OTP sub-flow surface
		submitOtpCode,
		resendOtpCode,
		resetOtpContext,
		otpVerifying,
		otpCodeError,
		otpChannel,
		otpMaskedContact,
	};
};
